import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useBrains from '@/hooks/useBrains';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import useLogs from '@/hooks/useLogs';
import { useModalTryIt } from '@/hooks/useModalTryIt';
import { cn } from '@/lib/utils';
import styles from '@/modules/tryIt/components/ModalTryIt/ModalTryIt.module.scss';
import { setSessionSource } from '@/modules/tryIt/redux/actions';
import { selectIsAmended, selectTryItSessionId } from '@/modules/tryIt/redux/selectors';
import { popModal } from '@/redux/modals/actions';
import { delay } from '@/util/util';

import { useReplayMessagesSync } from './hooks/useReplayMessagesSync';
import { LeftSide } from './LeftSide/LeftSide';
import { Messages } from './LeftSide/Messages/Messages';
import { enterReplay, enterTest } from './redux/actions';
import { selectIsReplay, selectMode } from './redux/selectors';
import { ReplayKeyboardHint } from './ReplayKeyboardHint/ReplayKeyboardHint';
import { RightSide } from './RightSide/RightSide';

type Props = {
  brainId?: string;
  sessionId?: string;
  sessionStart?: string;
  tryItSessionId?: string;
  collectionLanguage?: string;
  showNavigationHint?: boolean;
};

/**
 * Base shell for the redesigned Try It modal. Owns the `<Dialog>` and mounts
 * exactly two children — `<LeftSide>` (chat) and `<RightSide>` (info panels) —
 * plus the replay keyboard hint. All transitions go through the `tryIt`
 * state machine; this component only enters/exits the machine and bridges the
 * Apollo→Redux replay sync.
 */
export const ModalTryIt = memo(
  ({ brainId, sessionId, sessionStart, collectionLanguage, showNavigationHint }: Props) => {
    const { t } = useTranslation();
    const [isNudging, setIsNudging] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const prevSessionIdRef = useRef(sessionId);
    const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const isSessionAmended = useSelector(selectIsAmended);
    // `mode` is initialized below from the props and is the source of truth.
    const isReplay = useSelector(selectIsReplay);
    const currentMode = useSelector(selectMode);
    const tryItSessionId = useSelector(selectTryItSessionId);

    const { clearSessionIdFromUrl } = useModalTryIt(sessionId);
    const { brain } = useBrains(brainId);
    const { markAsComplete } = useHomeCheckList();
    const session_id = sessionId ?? undefined;
    const { content, isLoading } = useLogs({ sessionId: session_id, sessionStart });
    const { brain: logBrain } = useBrains(content?.brain_parent_id);

    // The single Apollo→Redux bridge for replay: hydrates `state.tryIt.messages`
    // from the analytics GraphQL + translation endpoint. Must be called exactly
    // once at the modal root — every replay panel reads `selectMessages` once
    // the sync has populated it. No-op outside replay mode.
    useReplayMessagesSync({ sessionId: session_id, sessionStart });

    // Populate the `tryIt` slice so the children have something to read.
    //
    // Replay re-initializes whenever the incoming `sessionId` differs from what
    // the slice currently holds. Arrow-key / table navigation updates this
    // modal's props in place (`updateModal`, no remount), so gating only on
    // `mode === null` would leave the slice pinned to the first session and
    // every panel reading stale data. Re-dispatching `openReplay` resets the
    // machine to `loading`, and `useReplayMessagesSync` re-hydrates from the new
    // session's content. Re-entering the same mode is idempotent, so a
    // StrictMode double mount is harmless.
    // Layout effect: commit the mode before paint, else reopening test after a replay flashes the stale frame.
    useLayoutEffect(() => {
      if (sessionId) {
        const alreadyLoaded = currentMode?.kind === 'replay' && currentMode.sessionId === sessionId;
        if (alreadyLoaded) {
          return;
        }
        dispatch(enterReplay({ sessionId, sessionStart, language: collectionLanguage }));
        return;
      }
      // Test mode persists across close/reopen: only (re)initialize when there's
      // no existing test session for THIS brain (first open, a different brain, or
      // after a replay). Reopening the same brain keeps the conversation.
      const hasTestSession = currentMode?.kind === 'test' && currentMode.brainId === brainId;
      if (brainId && !hasTestSession) {
        dispatch(enterTest({ tryItSessionId: uuidv4(), brainId }));
      }
    }, [currentMode, sessionId, brainId, sessionStart, collectionLanguage, dispatch]);

    // No unmount wipe: the slice persists across close/reopen so the test
    // conversation is preserved. It's reset only on the next open — when the
    // brain changes (test init above) or a replay is opened (`openReplay`).

    const handleInvalidateSessionCache = useCallback(() => {
      apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'get_session_content' });
      apolloClient.cache.gc();
    }, [apolloClient]);

    const handleBlurModal = useCallback(async () => {
      // Strip the session id from the URL via a replace so the back button
      // doesn't return the user to the open modal.
      navigate(clearSessionIdFromUrl(), { replace: true });
      if (isSessionAmended) {
        // Wait for the close animation before evicting, otherwise the modal
        // flashes stale data on the way out.
        await delay(200);
        handleInvalidateSessionCache();
      }
    }, [navigate, clearSessionIdFromUrl, isSessionAmended, handleInvalidateSessionCache]);

    const handleCloseModal = useCallback(() => {
      dispatch(popModal());
      handleBlurModal();
    }, [dispatch, handleBlurModal]);

    // When `sessionId` changes in replay mode (arrow-key navigation), flip
    // `isTransitioning` on for 250ms so the content cross-fades instead of
    // swapping instantly. The ref guard skips the first run.
    useEffect(() => {
      if (!isReplay || prevSessionIdRef.current === sessionId) {
        return;
      }
      prevSessionIdRef.current = sessionId;
      setIsTransitioning(true);
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = setTimeout(() => setIsTransitioning(false), 250);
      return () => clearTimeout(transitionTimerRef.current);
    }, [sessionId, isReplay]);

    // Listen for `replay-boundary-nudge` (fired when arrow nav hits the first or
    // last session) and toggle `isNudging` for 400ms to trigger the shake.
    useEffect(() => {
      const handleNudge = () => {
        setIsNudging(true);
        clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = setTimeout(() => setIsNudging(false), 400);
      };
      window.addEventListener('replay-boundary-nudge', handleNudge);
      return () => {
        window.removeEventListener('replay-boundary-nudge', handleNudge);
        clearTimeout(nudgeTimerRef.current);
      };
    }, []);

    // Keep the session source in sync (channel / desk / language).
    useEffect(() => {
      if (content && (logBrain || brain)) {
        dispatch(
          setSessionSource({
            deskId: content?.desk_id,
            integrationId: content?.integration_id,
            channel: content?.channel,
            sessionId: content?.session_id || tryItSessionId,
            language: logBrain?.language || brain?.language,
          })
        );
      }
    }, [brain, content, dispatch, logBrain, tryItSessionId]);

    useEffect(() => {
      if (isReplay && sessionId) {
        markAsComplete(AccountUserPrefsEnum.REVIEW_CONVERSATION);
      }
    }, [isReplay, sessionId, markAsComplete]);

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        handleCloseModal();
      }
    };

    return (
      <>
        <Dialog open onOpenChange={handleOpenChange}>
          <DialogContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            // No description; opt out of the missing-description warning.
            aria-describedby={undefined}
            className={cn(
              'flex h-192 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-6xl flex-row gap-4 overflow-hidden rounded-xl border border-border bg-subtle p-1 sm:max-w-6xl',
              '[&>button]:hidden',
              isNudging && styles.nudge
            )}
          >
            <DialogTitle className="sr-only">{t('try_it.try_it')}</DialogTitle>
            <LeftSide collectionLanguage={collectionLanguage} onClose={handleCloseModal}>
              <div
                className={cn(
                  'h-full transition-opacity duration-200',
                  isTransitioning && 'opacity-0',
                  isReplay && isLoading && 'opacity-0'
                )}
              >
                <Messages />
              </div>
            </LeftSide>

            <RightSide
              onClose={handleCloseModal}
              className={cn('transition-opacity duration-200', isTransitioning && 'opacity-0')}
            />
          </DialogContent>
        </Dialog>

        {isReplay && showNavigationHint && <ReplayKeyboardHint />}
      </>
    );
  }
);

ModalTryIt.displayName = 'ModalTryIt';
